// Modal de definição de senha pelo admin (PO1-2021).
// Padrão de Modal: modal-align-top + fade={false} + div header customizado.
//
// Suporta:
//   - Senha manual (input + validação local básica + 400 do servidor)
//   - Senha aleatória (gerada no backend, exibida UMA VEZ pro admin copiar)
//   - Flag "Deve alterar a senha no próximo login" (grava ShouldChangePasswordOnNextLogin)
//
// Política de senha (espelhada do backend PasswordPolicy.js — feedback rápido
// pra UX; o backend valida de novo): 8 chars + 3 das 4 classes.

import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";

import { setUserPassword } from "services/usersApi";
import { useToast } from "hooks/toast";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";

function classesPresentes(senha) {
  const s = String(senha || "");
  let n = 0;
  if (/[a-z]/.test(s)) n += 1;
  if (/[A-Z]/.test(s)) n += 1;
  if (/[0-9]/.test(s)) n += 1;
  if (/[^A-Za-z0-9]/.test(s)) n += 1;
  return n;
}

function validateLocal(senha) {
  if (!senha || senha.length < 8) {
    return "A senha deve ter pelo menos 8 caracteres.";
  }
  if (classesPresentes(senha) < 3) {
    return "A senha deve conter pelo menos 3 dos seguintes: minúscula, maiúscula, número, símbolo.";
  }
  return null;
}

export default function SetPasswordModal({ isOpen, user, onClose }) {
  const [useRandom, setUseRandom] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generated, setGenerated] = useState(null); // senha aleatória retornada
  const [copied, setCopied] = useState(false);
  const { addToast } = useToast();

  // Reset ao abrir / trocar de user
  useEffect(() => {
    if (!isOpen) return;
    setUseRandom(false);
    setMustChange(false);
    setPassword("");
    setError(null);
    setGenerated(null);
    setCopied(false);
  }, [isOpen, user]);

  const handleSubmit = useCallback(async () => {
    if (!user) return;
    setError(null);

    if (!useRandom) {
      const v = validateLocal(password);
      if (v) {
        setError(v);
        return;
      }
    }

    setSaving(true);
    try {
      const result = await setUserPassword(user.id, {
        password: useRandom ? undefined : password,
        randomPassword: useRandom,
        mustChangeOnNextLogin: mustChange,
      });
      addToast({
        place: "tr",
        type: "success",
        title: "Senha definida",
        description: useRandom
          ? "Senha aleatória gerada — copie e entregue ao usuário."
          : "Senha atualizada com sucesso.",
        icon: "tim-icons icon-check-2",
      });
      if (useRandom && result.generatedPassword) {
        setGenerated(result.generatedPassword);
      } else {
        onClose();
      }
    } catch (err) {
      console.error("[SetPasswordModal] submit:", err);
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        "Erro ao definir senha";
      setError(message);
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: message,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSaving(false);
    }
  }, [user, useRandom, password, mustChange, addToast, onClose]);

  const handleCopy = useCallback(async () => {
    if (!generated) return;
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(generated);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (_) {
      // Clipboard pode estar bloqueada — usuário copia manualmente.
    }
  }, [generated]);

  const handleDone = useCallback(() => {
    setGenerated(null);
    onClose();
  }, [onClose]);

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      modalClassName="modal-align-top"
      fade={false}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: AUTOPILOT_COLORS.insetSurface,
          borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
          padding: "16px 20px",
        }}
      >
        <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, fontSize: 15 }}>
          Definir senha — {user.name}
        </span>
        <button
          onClick={generated ? handleDone : onClose}
          style={{
            background: "none",
            border: "none",
            color: AUTOPILOT_COLORS.title,
            fontSize: 22,
            lineHeight: 1,
            cursor: "pointer",
            padding: "0 4px",
          }}
          aria-label="Fechar"
        >
          ×
        </button>
      </div>

      <ModalBody>
        {/* Estado pós-geração: mostra a senha gerada */}
        {generated ? (
          <div>
            <p style={{ color: AUTOPILOT_COLORS.muted, marginBottom: 12 }}>
              Senha aleatória gerada. Copie agora e entregue ao usuário — ela
              não será exibida novamente.
            </p>
            <div
              style={{
                background: AUTOPILOT_COLORS.insetSurface,
                border: `1px solid ${AUTOPILOT_COLORS.border}`,
                borderRadius: 4,
                padding: "12px 14px",
                fontFamily: "monospace",
                fontSize: 16,
                color: AUTOPILOT_COLORS.title,
                letterSpacing: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{generated}</span>
              <Button color="info" size="sm" onClick={handleCopy}>
                {copied ? "Copiado!" : "Copiar"}
              </Button>
            </div>
            {mustChange && (
              <p style={{ color: AUTOPILOT_COLORS.statusWarning, marginTop: 12, fontSize: 12 }}>
                ⚠ O usuário será obrigado a trocar a senha no próximo login.
              </p>
            )}
          </div>
        ) : (
          <div>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={useRandom}
                  onChange={(e) => setUseRandom(e.target.checked)}
                />
                <span className="form-check-sign" />
                Gerar senha aleatória (12 caracteres)
              </Label>
            </FormGroup>

            {!useRandom && (
              <FormGroup style={{ marginTop: 12 }}>
                <Label>Senha</Label>
                <Input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres + 3 classes"
                  invalid={!!error}
                />
              </FormGroup>
            )}

            <FormGroup check style={{ marginTop: 12 }}>
              <Label check>
                <Input
                  type="checkbox"
                  checked={mustChange}
                  onChange={(e) => setMustChange(e.target.checked)}
                />
                <span className="form-check-sign" />
                Deve alterar a senha no próximo login
              </Label>
            </FormGroup>

            {error && (
              <div style={{ color: AUTOPILOT_COLORS.statusDanger, marginTop: 12, fontSize: 13 }}>
                {error}
              </div>
            )}

            <div
              style={{
                color: AUTOPILOT_COLORS.muted,
                fontSize: 11,
                marginTop: 16,
                lineHeight: 1.5,
              }}
            >
              A nova senha vale para o Hub e para o Flow. As sessões abertas
              do usuário são encerradas.
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        {generated ? (
          <Button color="success" onClick={handleDone}>
            Concluir
          </Button>
        ) : (
          <>
            <Button color="secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button color="success" onClick={handleSubmit} disabled={saving}>
              {saving ? "Salvando…" : useRandom ? "Gerar senha" : "Definir"}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}
